<template>
  <section class="container-fluid mt-5 py-5">
    <div class="container mt-5 px-3">
      <!-- Header Section -->
      <div class="row align-items-center mb-3">
        <div class="col-md-6 mb-3 mb-md-0">
          <div class="d-flex align-items-center gap-3">
            <h2 class="mb-1 d-flex align-items-center text-light fs-1">
              Catálogo de Semillas<span class="text-primary">.</span>
            </h2>
            <button 
              class="btn btn-outline-primary"
              @click="forceRefresh"
              :disabled="isLoading"
            >
              <i class="fas fa-sync-alt me-2"></i>
              {{ isLoading ? 'Actualizando...' : 'Actualizar' }}
            </button>
          </div>
          <p class="text-muted mb-0 fs-5">
            <i class="fas fa-box-open me-1 text-primary"></i>
            {{ totalProducts }} productos disponibles
          </p>
          <!-- Filters Section -->
        <div class="col-md-10 my-3 mb-md-0">
      </div>
        </div>
        <div class="col-md-6 d-lg-flex justify-content-lg-end">
          <div class="d-flex gap-3 flex-column">
            <a href="/admin/productos/nuevo/" class="btn btn-primary btn-lg">
              <i class="fas fa-plus me-2"></i>
              Agregar blíster
            </a>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="d-flex flex-wrap gap-3 align-items-center">
            <!-- Search input -->
            <div class="flex-grow-1">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  class="form-control border-start-0 ps-0 text-black"
                  v-model="filters.search"
                  placeholder="Buscar por nombre o marca..."
                  @input="debounceSearch"
                />
              </div>
            </div>
            
            <!-- Filter buttons -->
            <div class="position-relative">
              <select class="form-select" v-model="filters.brand" style="max-width: 200px;" :disabled="isLoadingBrands">
                <option value="">{{ isLoadingBrands ? 'Cargando bancos...' : 'Todos los bancos' }}</option>
                <option v-for="brand in uniqueBrands" :key="brand" :value="brand">
                  {{ brand }}
                </option>
              </select>
              <button 
                v-if="filters.brand" 
                type="button" 
                class="btn btn-sm btn-outline-secondary position-absolute top-50 end-0 translate-middle-y me-2"
                @click="clearBrandSelection"
                title="Limpiar marca seleccionada"
                style="padding: 0.15rem 0.3rem; font-size: 0.75rem;"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>

            <select class="form-select" v-model="filters.type" style="max-width: 200px;">
              <option value="">Todos los tipos</option>
              <option value="auto">Automáticas</option>
              <option value="fem">Feminizadas</option>
              <option value="regular">Regulares</option>
            </select>

            <select class="form-select" v-model="filters.stock" style="max-width: 200px;">
              <option value="">Todos los stocks</option>
              <option value="with-stock">Con stock</option>
              <option value="low-stock">Stock bajo</option>
              <option value="out-of-stock">Sin stock</option>
            </select>

            <select class="form-select" v-model="filters.availability" style="max-width: 200px;">
              <option value="">Todas las disponibilidades</option>
              <option value="available">Disponibles</option>
              <option value="unavailable">No disponibles</option>
            </select>

            <select class="form-select" v-model="filters.priceStatus" style="max-width: 200px;">
              <option value="">Todos los precios</option>
              <option value="no-price">Sin precio</option>
              <option value="with-price">Con precio</option>
            </select>

            <button 
              class="btn btn-primary"
              @click="applyFilters"
              :disabled="isLoading"
            >
              <i class="fas fa-search me-1"></i>
              {{ isLoading ? 'Cargando...' : 'Aplicar filtros' }}
            </button>

            <button 
              class="btn btn-outline-secondary"
              @click="clearFilters"
            >
              <i class="fas fa-times me-1"></i>
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="row g-4">
        <div v-for="n in 8" :key="n" class="col-lg-4">
          <div class="skeleton-loader">
            <div class="skeleton-text w-50"></div>
            <div class="skeleton-text w-75"></div>
            <div class="skeleton-text w-75"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
        <button @click="fetchProducts" class="btn btn-outline-danger btn-sm ms-3">
          <i class="fas fa-sync-alt me-1"></i>Reintentar
        </button>
      </div>

      <!-- Products List -->
      <div v-else class="row g-4">
        <div v-for="product in products" :key="product.id" class="col-md-6 col-lg-4">
          <div class="card bg-dark text-light">
            <div class="card-body d-flex flex-column">
              <!-- Product header -->
              <div class="d-flex align-items-center mb-3">
                <img
                  :src="product.image"
                  :alt="product.name"
                  class="img-thumbnail me-3"
                  style="width: 100px; height: 100px; object-fit: cover;"
                />
                <div>
                  <h5 class="mb-1 fs-6 fw-bold">{{ product.name }}</h5>
                  <p class="mb-1 text-muted">{{ truncateText(product.description, 40) }}</p>
                  <small class="text-muted">{{ product.brand }}</small>
                </div>
              </div>
              <!-- Product details -->
              <div class="d-flex justify-content-between align-items-end flex-grow-1">
                <div class="d-flex flex-column gap-1">
                  <div class="d-flex gap-1 flex-wrap">
                    <span class="badge bg-primary me-1">{{ getTypeLabel(product.type) }}</span>
                    <!-- Badge de disponibilidad -->
                    <button
                      class="badge border-0 p-0"
                      :class="product.available !== false ? 'bg-success' : 'bg-danger'"
                      @click="toggleAvailability(product)"
                      :title="product.available !== false ? 'Producto disponible (click para ocultar)' : 'Producto no disponible (click para mostrar)'"
                      style="cursor: pointer;"
                    >
                      <i class="fas" :class="product.available !== false ? 'fa-eye' : 'fa-eye-slash'"></i>
                      {{ product.available !== false ? 'Disponible' : 'No disponible' }}
                    </button>
                  </div>
                  <!-- Mostrar badges de stock para cada blister -->
                  <div v-if="product.blisters && product.blisters.length > 0" class="d-flex flex-column gap-1">
                    <div v-for="blister in product.blisters" :key="blister.id" class="d-flex align-items-center gap-1">
                      <span 
                        class="badge"
                        :class="getStockBadgeClass(blister.stock)"
                      >
                        {{ blister.seed_count }} sem: {{ getStockLabel(blister.stock) }}
                      </span>
                      <span 
                        v-if="blister.discount > 0"
                        class="badge bg-success"
                        title="Tiene descuento activo"
                      >
                        -{{ Math.round(blister.discount) }}%
                      </span>
                    </div>
                  </div>
                </div>
                <div class="d-flex flex-column align-items-end gap-1">
                  <!-- Mostrar rango de precios -->
                  <div class="price mb-2 text-success" v-if="product.blisters && product.blisters.length > 0">
                    <span v-if="getMinPrice(product) === getMaxPrice(product)">
                      ${{ formatPrice(getMinPrice(product)) }}
                    </span>
                    <span v-else>
                      ${{ formatPrice(getMinPrice(product)) }} - ${{ formatPrice(getMaxPrice(product)) }}
                    </span>
                  </div>
                  <div class="d-flex gap-2 w-100">
                    <router-link 
                      class="btn s btn-secondary flex-grow-1" 
                      :to="{ name: 'ProductEdit', params: { productId: product.id }}"
                    >
                      <i class="fas fa-edit me-1"></i>
                      Editar
                    </router-link>
                    <button 
                      class="btn btn-danger" 
                      @click="confirmDelete(product)"
                      title="Eliminar producto"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                  <router-link 
                    class="btn btn-primary w-100" 
                    :to="{ name: 'ProductDetails', params: { id: product.id }}"
                  >
                    <i class="fas fa-eye me-1"></i>
                    Publicación
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="!isLoading && hasNextPage" class="d-flex justify-content-center mt-4">
        <button 
          class="btn btn-primary" 
          @click="loadMoreProducts"
          :disabled="isLoading"
        >
          <i class="fas fa-plus me-2"></i>
          Cargar más productos
        </button>
      </div>

      <!-- Debug info (temporal) -->
      <div class="text-center mt-2 text-muted small">
        Página actual: {{ currentPage }} | Total productos: {{ totalProductsCount }} | 
        Productos mostrados: {{ products.length }} | 
        Hay más páginas: {{ hasNextPage ? 'Sí' : 'No' }} |
        Cargando: {{ isLoading ? 'Sí' : 'No' }}
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && products.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-seedling fa-3x mb-3"></i>
        <h4>No se encontraron productos</h4>
        <p>Prueba con otros filtros de búsqueda</p>
      </div>
    </div>
    
    <!-- Confirmation Modal -->
    <div class="modal fade mt-5" id="deleteConfirmModal" tabindex="-1" aria-labelledby="deleteConfirmModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-light">
          <div class="modal-header border-secondary">
            <h5 class="modal-title fs-4" id="deleteConfirmModalLabel">Confirmar eliminación</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">¿Está seguro de que desea eliminar el producto <strong>{{ productToDelete?.name }}</strong>?</p>
            <p class="text-danger mt-2 mb-0">
              <i class="fas fa-exclamation-triangle me-1"></i>
              Esta acción no se puede deshacer y eliminará todos los blisters asociados.
            </p>
          </div>
          <div class="modal-footer border-secondary">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button 
              type="button" 
              class="btn btn-danger"
              @click="handleDelete" 
              :disabled="isDeleting"
            >
              <span v-if="isDeleting" class="spinner-border spinner-border-sm me-1"></span>
              {{ isDeleting ? 'Eliminando...' : 'Eliminar producto' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { deleteProduct, getAllProductsForAdmin, toggleProductAvailability } from '@/services/products';
import { getBrands } from '@/services/brands';
import { useToast } from 'vue-toastification';
import { Modal } from 'bootstrap';

const toast = useToast();

// Estado
const products = ref([]);
const brands = ref([]); // Nueva variable para todas las marcas
const isLoading = ref(true);
const isLoadingBrands = ref(true);
const error = ref(null);
const productToDelete = ref(null);
const isDeleting = ref(false);
let deleteModal = null;

const ITEMS_PER_PAGE = 20;
const currentPage = ref(1);
const totalProductsCount = ref(0);
const hasNextPage = ref(false);
const hasActiveFilters = ref(false); // Nueva variable para rastrear filtros activos
const filters = ref({
  search: '',
  brand: '',
  type: '',
  stock: '',
  availability: '',
  priceStatus: ''
});

// Computed Properties
const totalProducts = computed(() => totalProductsCount.value || products.value.length);

const uniqueBrands = computed(() => {
  return brands.value.map(brand => brand.brand_name).sort();
});

// Comentado para usar botón de aplicar filtros manualmente
// watch(filters, () => {
//   currentPage.value = 1;
//   fetchProducts(1, true);
// }, { deep: true });

const loadMoreProducts = async () => {
  if (hasNextPage.value && !isLoading.value) {
    await fetchProducts(currentPage.value + 1, false, hasActiveFilters.value);
  }
};

const fetchBrands = async () => {
  try {
    isLoadingBrands.value = true;
    const brandsData = await getBrands();
    brands.value = brandsData;
    console.log('Marcas cargadas para filtros:', brands.value);
  } catch (err) {
    console.error('Error al cargar las marcas:', err);
    brands.value = [];
  } finally {
    isLoadingBrands.value = false;
  }
};

const fetchProducts = async (page = 1, resetProducts = true, useFilters = false) => {
  try {
    isLoading.value = true;
    error.value = null;
    
    if (resetProducts) {
      products.value = [];
      currentPage.value = 1;
    }
    
    // Preparar filtros para enviar al backend
    const filtersToSend = useFilters ? {
      search: filters.value.search,
      brand: filters.value.brand,
      type: filters.value.type,
      availability: filters.value.availability,
      stockFilter: filters.value.stock,  // Cambio de 'stock' a 'stockFilter' para coincidir con el backend
      priceStatus: filters.value.priceStatus
    } : {};
    
    const response = await getAllProductsForAdmin(false, page, ITEMS_PER_PAGE, filtersToSend);
    console.log('Admin products response:', response);
    
    // Handle paginated response
    if (response && response.results) {
      console.log('Paginated response received:', {
        page,
        resultsCount: response.results.length,
        totalCount: response.count,
        hasNext: !!response.next,
        nextUrl: response.next
      });
      
      if (resetProducts) {
        products.value = response.results;
      } else {
        products.value = [...products.value, ...response.results];
      }
      totalProductsCount.value = response.count || response.results.length;
      hasNextPage.value = !!response.next;
      currentPage.value = page;
      
      console.log('After setting hasNextPage:', hasNextPage.value);
    } else if (Array.isArray(response)) {
      console.log('Array response received:', response.length);
      products.value = response;
      totalProductsCount.value = response.length;
      hasNextPage.value = false;
    } else {
      products.value = [];
      totalProductsCount.value = 0;
      hasNextPage.value = false;
    }
    
  } catch (err) {
    error.value = err.message || 'Error al cargar los productos';
    console.error('Error fetching admin products:', err);
    products.value = [];
    totalProductsCount.value = 0;
    hasNextPage.value = false;
  } finally {
    isLoading.value = false;
  }
};

const forceRefresh = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    
    const response = await getAllProductsForAdmin(true, 1, ITEMS_PER_PAGE); // Forzar refresh de primera página
    console.log('Admin refresh response:', response);
    
    // Handle paginated response
    if (response && response.results) {
      products.value = response.results;
      totalProductsCount.value = response.count || response.results.length;
      hasNextPage.value = !!response.next;
      currentPage.value = 1;
    } else if (Array.isArray(response)) {
      products.value = response;
      totalProductsCount.value = response.length;
      hasNextPage.value = false;
    } else {
      products.value = [];
      totalProductsCount.value = 0;
      hasNextPage.value = false;
    }
    
    toast.success('Productos actualizados');
  } catch (err) {
    error.value = err.message;
    console.error('Error refreshing admin products:', err);
    toast.error('Error al actualizar los productos');
    products.value = [];
    totalProductsCount.value = 0;
    hasNextPage.value = false;
  } finally {
    isLoading.value = false;
  }
};

const confirmDelete = (product) => {
  productToDelete.value = product;
  if (!deleteModal) {
    deleteModal = new Modal(document.getElementById('deleteConfirmModal'));
  }
  deleteModal.show();
};

const handleDelete = async () => {
  if (!productToDelete.value) return;
  
  try {
    isDeleting.value = true;
    await deleteProduct(productToDelete.value.id);
    
    // Eliminar el producto del arreglo local
    if (Array.isArray(products.value)) {
      products.value = products.value.filter(p => p.id !== productToDelete.value.id);
    }
    
    // Mostrar notificación de éxito
    toast.success(`El producto "${productToDelete.value.name}" ha sido eliminado`);
    
    // Cerrar el modal
    deleteModal.hide();
    productToDelete.value = null;
  } catch (error) {
    console.error('Error al eliminar el producto:', error);
    toast.error(`Error al eliminar el producto: ${error.message}`);
  } finally {
    isDeleting.value = false;
  }
};

// Helper functions
const debounceSearch = (() => {
  let timeout;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      // La búsqueda se realiza automáticamente por el computed property
    }, 300);
  };
})();

const formatPrice = (price) => {
  return price.toLocaleString('es-CL');
};

const truncateText = (text, length) => {
  if (!text) return '';
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
};

const getTypeLabel = (type) => ({
  'auto': 'Automática',
  'fem': 'Feminizada'
}[type] || type);

const getStockBadgeClass = (stock) => {
  if (stock === 0) return 'bg-danger';
  if (stock < 5) return 'bg-warning';
  return 'bg-success';
};

const getStockLabel = (stock) => {
  if (stock === 0) return 'Sin stock';
  if (stock < 5) return `${stock} unid.`;
  return 'En stock';
};

const getMinPrice = (product) => {
  if (!product.blisters?.length) return 0;
  return Math.min(...product.blisters.map(b => parseFloat(b.price)));
};

const getMaxPrice = (product) => {
  if (!product.blisters?.length) return 0;
  return Math.max(...product.blisters.map(b => parseFloat(b.price)));
};

const applyFilters = async () => {
  // Verificar si hay filtros activos
  hasActiveFilters.value = Object.values(filters.value).some(value => value && value.trim() !== '');
  currentPage.value = 1;
  await fetchProducts(1, true, hasActiveFilters.value);
};

const clearFilters = () => {
  filters.value = {
    search: '',
    brand: '',
    type: '',
    stock: '',
    availability: '',
    priceStatus: ''
  };
  hasActiveFilters.value = false;
  currentPage.value = 1;
  fetchProducts(1, true, false); // false para no usar filtros
};

// Función para limpiar solo la selección de marca
const clearBrandSelection = () => {
  filters.value.brand = '';
  currentPage.value = 1;
};

// Función para cambiar la disponibilidad del producto
const toggleAvailability = async (product) => {
  try {
    const newAvailability = product.available === false ? true : false;
    
    console.log('Cambiando disponibilidad del producto:', product.name, 'a:', newAvailability);
    
    await toggleProductAvailability(product.id, newAvailability);
    
    // Actualizar el producto en la lista local
    const productIndex = products.value.findIndex(p => p.id === product.id);
    if (productIndex !== -1) {
      products.value[productIndex].available = newAvailability;
    }
    
    const statusText = newAvailability ? 'disponible' : 'no disponible';
    toast.success(`El producto "${product.name}" ahora está ${statusText}`);
  } catch (error) {
    console.error('Error al cambiar la disponibilidad:', error);
    toast.error(`Error al cambiar la disponibilidad: ${error.message}`);
  }
};

// Lifecycle
onMounted(async () => {
  // Cargar marcas y productos en paralelo
  await Promise.all([
    forceRefresh(), // Cargar productos con refresh forzado  
  fetchBrands()
  ]);
  
  // Actualizar cada 5 minutos
  const refreshInterval = setInterval(() => {
    fetchProducts(1, true, hasActiveFilters.value); // Usar filtros activos si los hay
    fetchBrands(); // También actualizar las marcas periódicamente
  }, 5 * 60 * 1000);

  // Limpiar el intervalo cuando el componente se destruye
  onUnmounted(() => {
    clearInterval(refreshInterval);
  });
});
</script>

<style scoped>
.card {
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease-in-out;
}

.card:hover {
  transform: translateY(-0.25rem);
}

.img-thumbnail {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 0.25rem;
}

.price {
  font-size: 0.9rem;
}

.badge {
  font-size: 0.75rem;
  white-space: nowrap;
}

.d-flex.align-items-center.gap-1 {
  flex-wrap: wrap;
}

/* Skeleton Loading */
.skeleton-loader {
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.skeleton-text {
  height: 20px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.pagination {
  --bs-pagination-color: var(--bs-primary);
  --bs-pagination-hover-color: var(--bs-primary);
  --bs-pagination-focus-color: var(--bs-primary);
  --bs-pagination-active-bg: var(--bs-primary);
  --bs-pagination-active-border-color: var(--bs-primary);
}

.form-select {
  background-color: var(--bs-dark);
  color: var(--bs-light);
  border-color: var(--bs-gray-700);
}

.form-select:focus {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
}

/* Estilos para los filtros */
.form-select {
  background-color: var(--black-color-3);
  color: var(--white-color-2);
  border: 1px solid var(--dark-color);
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.form-select:hover {
  border-color: var(--primary-color);
}

.form-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.25rem var(--primary-color-subtle);
}

.form-select:disabled {
  background-color: var(--black-color-2);
  color: var(--secondary-color);
  cursor: not-allowed;
  opacity: 0.6;
}

/* Estilos para la paginación */
.pagination {
  margin-top: 2rem;
}

.pagination .page-link {
  background-color: var(--black-color-3);
  border: 1px solid var(--dark-color);
  color: var(--white-color-2);
  padding: 0.5rem 1rem;
  margin: 0 0.2rem;
  transition: all 0.3s ease;
}

.pagination .page-link:hover {
  background-color: var(--primary-color);
  color: var(--white-color);
  border-color: var(--primary-color);
}

.pagination .page-item.active .page-link {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--white-color);
}

.pagination .page-item.disabled .page-link {
  background-color: var(--black-color-2);
  border-color: var(--dark-color);
  color: var(--secondary-color);
}

/* Botón de limpiar filtros */
.btn-outline-secondary {
  background-color: transparent;
  color: var(--white-color-2);
  border: 1px solid var(--secondary-color);
  transition: all 0.3s ease;
}

.btn-outline-secondary:hover {
  background-color: var(--secondary-color);
  color: var (--white-color);
  border-color: var(--secondary-color);
}

/* Campo de búsqueda */
.input-group .form-control {
  border: 1px solid var(--dark-color);
  color: var(--white-color-2);
  font-size: 0.9rem;
}

.input-group .form-control:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.25rem var(--primary-color-subtle);
}

.input-group-text {
  background-color: var(--black-color-3);
  border: 1px solid var (--dark-color);
  color: var(--white-color-2);
}

/* Ajustes responsivos */
@media (max-width: 768px) {
  /* Contenedor de filtros */
  .d-flex.flex-wrap.gap-3 {
    flex-direction: column;
    width: 100%;
    gap: 0.75rem !important;
  }

  /* Ajustes para todos los elementos de filtrado */
  .form-select, 
  .btn-outline-secondary,
  .input-group,
  .flex-grow-1 {
    width: 100% !important;
    max-width: 100% !important;
    margin-bottom: 0.75rem;
  }

  /* Asegurar que el contenedor de búsqueda tenga el mismo ancho */
  .flex-grow-1 {
    flex-grow: 0 !important;
  }

  /* Asegurar que el input de búsqueda tenga el mismo estilo */
  .input-group .form-control,
  .input-group-text {
    height: 38px; /* Mismo alto que los selects */
  }

  /* Ajustar el espaciado del contenedor principal de filtros */
  .row.mb-4 .col-12 {
    padding: 0 1rem;
  }

  /* Ajustar el tamaño de la paginación en mobile */
  .pagination .page-link {
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
}

/* Asegurar consistencia visual en elementos de filtrado */
.form-select,
.input-group .form-control,
.btn-outline-secondary {
  height: 38px;
  line-height: 1.5;
}

/* Estilos para el botón de eliminación */
.btn-danger {
  background-color: var(--bs-danger);
  border-color: var(--bs-danger);
}

.btn-danger:hover {
  background-color: #bb2d3b;
  border-color: #b02a37;
}

/* Estilos para el modal de confirmación */
.modal-content {
  border: 1px solid var(--dark-color);
}

@media (max-width: 768px) {
  /* ...existing code... */
  
  /* Ajustes para los botones de acción en mobile */
  .d-flex.gap-2 {
    width: 100%;
  }
}
</style>